import { memo, useMemo } from "react";
import { rgbToCss } from "utils/rgbToCss";
import { getCircleSize } from "../utils";
import { PolygonLabel } from "./PolygonLabel";
import {
  CREATION_RGB,
  NODE_RADIUS_FACTOR,
  SEGMENT_HIT_STROKE_WIDTH,
  SELECTED_NODE_HALO_FACTOR,
  SELECTED_NODE_HALO_STROKE,
} from "../RoutesCreationLayer/constants";
import {
  POLYGON_FILL_OPACITY,
  POLYGON_STROKE_WIDTH,
  SELECTED_POLYGON_FILL_OPACITY,
  SELECTED_POLYGON_HALO_COLOR,
  SELECTED_POLYGON_HALO_WIDTH,
  SELECTED_POLYGON_STROKE_WIDTH,
} from "./constants";
import type { PolygonRenderProps } from "./types";

export const PolygonRender = memo(function PolygonRender({
  polygon,
  isInteractive,
  isEditable,
  selectedPointId,
  zoomLevel,
  circleSizeScale,
  onPolygonClick,
  onNodeMouseDown,
  onSegmentMouseDown,
}: PolygonRenderProps) {
  const color = rgbToCss(polygon.isDraft ? CREATION_RGB : polygon.rgb);
  const size = getCircleSize(zoomLevel) * circleSizeScale;
  const pointRadius = (size / 2) * NODE_RADIUS_FACTOR;
  const selectedHaloRadius = pointRadius * SELECTED_NODE_HALO_FACTOR;
  const showHandles = isEditable && polygon.isSelected;
  const isClickable = isInteractive && polygon.isLocked !== true;

  // The entry object is rebuilt on every hover and zoom, but its points array keeps identity.
  const polygonPoints = useMemo(
    () => polygon.points.map((point) => `${point.x},${point.y}`).join(" "),
    [polygon.points],
  );

  return (
    <g>
      {polygon.isSelected && (
        <polygon
          points={polygonPoints}
          fill="none"
          stroke={SELECTED_POLYGON_HALO_COLOR}
          strokeWidth={SELECTED_POLYGON_HALO_WIDTH}
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
          style={{ pointerEvents: "none" }}
        />
      )}
      <polygon
        points={polygonPoints}
        // Even-odd matches the backend's point-in-polygon semantics exactly.
        fillRule="evenodd"
        fill={color}
        fillOpacity={
          polygon.isSelected
            ? SELECTED_POLYGON_FILL_OPACITY
            : POLYGON_FILL_OPACITY
        }
        stroke={color}
        strokeWidth={
          polygon.isSelected
            ? SELECTED_POLYGON_STROKE_WIDTH
            : POLYGON_STROKE_WIDTH
        }
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        style={{
          pointerEvents: isClickable ? "all" : "none",
          cursor: isClickable ? "pointer" : undefined,
        }}
        onClick={
          isClickable
            ? (event) => {
                event.stopPropagation();
                onPolygonClick?.(polygon.id);
              }
            : undefined
        }
      />
      {polygon.label !== undefined && (
        <PolygonLabel
          polygon={polygon}
          zoomLevel={zoomLevel}
          circleSizeScale={circleSizeScale}
        />
      )}
      {showHandles &&
        polygon.points.map((point, index) => {
          const nextPoint = polygon.points[(index + 1) % polygon.points.length];
          return (
            <line
              key={`segment-hit-${index}`}
              x1={point.x}
              y1={point.y}
              x2={nextPoint.x}
              y2={nextPoint.y}
              stroke="transparent"
              strokeWidth={SEGMENT_HIT_STROKE_WIDTH}
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
              style={{ pointerEvents: "all", cursor: "copy" }}
              onMouseDown={(event) =>
                onSegmentMouseDown?.({
                  routeId: polygon.id,
                  edgeIndex: index,
                  event,
                })
              }
            />
          );
        })}
      {showHandles &&
        polygon.points.map((point, index) => {
          // Pseudo-node ids are index + 1, matching the drag adapter's contract.
          const pointId = index + 1;
          const isSelected = selectedPointId === pointId;
          return (
            <g key={`point-${pointId}`}>
              {isSelected && (
                <circle
                  cx={point.x}
                  cy={point.y}
                  r={selectedHaloRadius}
                  fill="none"
                  stroke={SELECTED_NODE_HALO_STROKE}
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                />
              )}
              <circle
                cx={point.x}
                cy={point.y}
                r={pointRadius}
                fill={color}
                fillOpacity={0.85}
                stroke={color}
                strokeWidth={2}
                vectorEffect="non-scaling-stroke"
                style={{ pointerEvents: "all", cursor: "grab" }}
                onMouseDown={(event) =>
                  onNodeMouseDown?.({
                    routeId: polygon.id,
                    nodeId: pointId,
                    event,
                  })
                }
              />
            </g>
          );
        })}
    </g>
  );
});
